<template>
  <div class="row">
    <div v-if="showThumbnail" class="col-xs-2 text-center">
      <div class="resource_thumbnail">
        <div
          v-if="screenshots.length > 0"
          class="resource_thumbnail_image__hover"
          @click="toggleModal(true)"
          role="button">
          <i class="fa fa-search-plus" aria-hidden="true"/>
          <br>
          <span tabindex="0">PREVIEW</span>
        </div>
        <div class="resource_thumbnail_image__back"/>
        <IKImageS3Wrapper
          class="resource_thumbnail_image"
          :aws-url="thumbnail_url"
          alt=""
          :transformation="[{width: 150}]" />
      </div>
      <a
        v-if="screenshots.length > 0"
        @click="toggleModal(true)"
        @keyup.enter="toggleModal(true)"
        class="btn link-button"
        tabindex="0"
        role="button"
        :aria-label="`Preview ${title}`">Preview
      </a>
    </div>

    <div :class="{'col-xs-10': showThumbnail, 'col-xs-12': !showThumbnail}">
      <div class="resource_panel">
        <span class="resource_type text-muted" v-if="showResourceType || typeHeader">{{ typeHeader || type }}</span>
        <span class="language-badge badge" v-if="language && language !== 'english'">{{ language | capitalize }}</span>
        <a
          v-if="collapsibleDescription"
          role="button"
          @click="showDescription = !showDescription"
        >
          <h3 class="resource_title"> {{ titleWithSet }}
            <i class="fa" :class="{'fa-caret-up': showDescription, 'fa-caret-down': !showDescription}"/>
          </h3>
        </a>
        <h3
          v-else
          class="resource_title"
          :style="newHomepage ? 'font-weight: 500': ''"
        >{{ titleWithSet }}</h3>

        <collapse v-model="showDescription" v-if="!omitDescription">
          <p class="gbm-body-1 resource_description">{{ description }}</p>
        </collapse>

        <div v-if="rationale && rationale.trim()" class="rationale-section">
          <p class="rationale-label">Why this result:</p>
          <p class="rationale-text">{{ rationale }}</p>
        </div>

        <ResourceFoundIn
          v-if="alignedContent"
          :aligned-content="alignedContent"
        />

        <slot name="resource-details">
          <p><small class="text-muted">Grade {{ grades.join(', ') }} · {{ displaySubjects.join(', ') }} <span v-if="num_pages > 0">· {{ num_pages }} pages</span></small></p>
        </slot>

        <ResourceDownloadButton
          style="display: inline-block; padding-right: 4px;"
          :new-homepage="newHomepage"
          :resource="{uuid, type, title, download_links, google_drive_links, language, set_number}"
          :position="position"
          @resourceDownloadClicked="$emit('resourceDownloadClicked')"
          @resourceDownloadOpened="$emit('resourceDownloadOpened')"/>

        <button
          v-if="!savePermissions.block && showSaveButton"
          class="btn btn-success d-inline-flex align-center"
          :class="newHomepage ? 'save-button' : ''"
          :aria-label="`Save ${title}`"
          @click="toggleStudentModal(true)">
          <span class="material-icons-round pr-1">save</span>
          <span class="mx-xxs">Save</span>
        </button>

        <ResourcePreviewModal
          v-if="showModal"
          @preview:close="toggleModal(false)"
          @resourcePreviewShown="$emit('resourcePreviewShown')"
          :uuid="uuid"
          :title="title"
          :type="type"
          :download-links="download_links"
          :google-drive-links="google_drive_links"
          :screenshots="screenshots"
          :num-pages="num_pages"
          :language="language"/>

        <SaveResourceModal :content-uuid="uuid" :open.sync="studentModalShown" />

      </div>
    </div>
  </div>
</template>

<script>
import ResourceDownloadButton from '@/components/ResourceDownloadButton'
import ResourcePreviewModal from '@/components/ResourcePreviewModal'
import SaveResourceModal from '@/components/SaveResourceModal'
import ResourceFoundIn from './search/ResourceFoundIn.vue'
import IKImageS3Wrapper from '@/imagekit/IKImageS3Wrapper'

import { mapGetters } from 'vuex'

import { actionPermissions, ACTION_TYPES, CONTENT_TYPES } from '@/util/access_visibility'
import { noSelMixin } from '@/contentprofile/no_sel_mixins'

export default {
  name: 'ResourcePreviewCard',
  components: {
    ResourceDownloadButton,
    ResourcePreviewModal,
    ResourceFoundIn,
    SaveResourceModal,
    IKImageS3Wrapper
  },
  mixins: [noSelMixin],
  props: {
    uuid: {
      required: true,
      type: String
    },
    title: {
      required: true,
      type: String
    },
    description: {
      required: true,
      type: String
    },
    type: {
      required: true,
      type: String
    },
    thumbnail_url: {
      required: true,
      type: String
    },
    grades: {
      required: true,
      type: Array
    },
    subjects: {
      required: true,
      type: Array
    },
    num_pages: {
      default: () => {
        return 0
      },
      type: Number
    },
    screenshots: {
      required: false,
      type: Array,
      default: () => {
        return []
      }
    },
    download_links: {
      required: true,
      type: Array
    },
    google_drive_links: {
      required: false,
      type: Array,
      default: () => {
        return []
      }
    },
    showSaveButton: {
      type: Boolean,
      default: true
    },
    showThumbnail: {
      type: Boolean,
      default: true
    },
    collapsibleDescription: {
      type: Boolean,
      default: false
    },
    language: {
      type: String,
      default: null
    },
    alignedContent: {
      type: Object,
      required: false,
      default: () => {}
    },
    showResourceType: {
      type: Boolean,
      default: true
    },
    omitDescription: {
      type: Boolean,
      default: false
    },
    newHomepage: {
      type: Boolean,
      default: false
    },
    position: {
      type: Number,
      required: false,
      default: null
    },
    typeHeader: {
      type: String,
      default: '',
    },
    set_number: {
      type: Number,
      default: 0
    },
    rationale: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      showModal: false,
      showDescription: this.collapsibleDescription ? false : true,
      studentModalShown: false
    }
  },
  eventContext() {
    return {
      resource_preview_type: 'card',
      resource_uuid: this.uuid,
      resource_title: this.title,
      resource_type: this.type,
      resource_grades: this.grades,
      resource_subjects: this.subjects,
      resource_language: this.language,
      ...(this.newHomepage ? {position: this.position, ref: 'home'} : {}),
    }
  },
  computed: {
    ...mapGetters('user', ['authInfo']),
    savePermissions() {
      return actionPermissions(ACTION_TYPES.SAVE, CONTENT_TYPES.RESOURCE, {}, this.authInfo)
    },
    displaySubjects() {
      return this.getResourceSubjectLabelsFromValues(this.subjects)
    },
    titleWithSet() {
      if (this.set_number && this.set_number !== 0) {
        return `${this.title} - Set ${this.set_number}`
      }
      return this.title
    }
  },
  methods: {
    toggleModal(val) {
      if (val !== undefined) {
        this.showModal = val
      } else {
        this.showModal = !this.showModal
      }
    },
    toggleStudentModal(open) {
      this.studentModalShown = open

      if (open) {
        this.$sendEvent('Clicked Save Resource')
        this.$emit('resourceSaved')
      }
    }
  }
}
</script>

<style scoped lang="less">
.col-xs-2 {
  padding: 24px;
  padding-right: 0;
}
.multilingual-resources .col-xs-2 {
  padding-top: 12px;
}
.resource_thumbnail {
  border: 0;
  position: relative;
}

.resource_thumbnail_image {
  display: block;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
  border: 1px solid #999;
  min-height: 60px;
  height: auto;
  max-width: 100%;
  vertical-align: middle;
}

.resource_thumbnail_image__back {
  position: absolute;
  transform: rotate(-5deg);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  z-index: -1;
  border: 1px solid #999;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.resource_panel {
  padding: 20px;
  padding-left: 8px;

  .btn-success {
    &:hover, &:focus {
      background-color: var(--green-button-hover);
    }
  }
}
.multilingual-resources .resource_panel {
  padding-top: 10px;
}

.resource_type {
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 500;
}
.resource_title,
.resource_description {
  color: #333;
  padding-bottom: var(--spacing-md);
  font-size: var(--text-md);
}
.resource_title {
  font-size: 18px;
  text-transform: none;
  font-weight: bold;
  line-height: 24px;
  margin-top: 0;
  padding-bottom: 0;
  margin-bottom: var(--spacing-md);
}
.resource_thumbnail:hover .resource_thumbnail_image__hover {
  visibility: visible;
  opacity: 0.7;
}

.resource_thumbnail_image__hover {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: -1px;
  padding-top: 15%;
  text-align: center;
  visibility: hidden;
  background: black;
  color: white;
}
.language-badge {
  margin-left: 6px;
  font-weight: normal;
  background-color: #585858;
}
.save-button {
  background-color: var(--green-default);
  padding: 0.3rem .7rem;
  margin-left: 5px;
  &:hover {
    background-color: var(--green-button-hover);
  }
}
.btn-success {
  padding: 6px 16px;
  line-height: 19px;
  .material-icons-round {
    font-size: 16px;
  }
}
.resource-details {
  margin-bottom: 1.25rem;
}
.type-header {
  font-size: 0.75rem;
  text-transform: uppercase;
  font-weight: 500;
  margin-bottom: var(--spacing-xxsg);
  padding-bottom: 0
}

.rationale-section {
  background-color: #f0f4f8;
  border-left: 4px solid #3b82f6;
  padding: 12px 16px;
  margin: 16px 0;
  border-radius: 4px;
}

.rationale-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.rationale-text {
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
  line-height: 1.5;
}
</style>
